import { useState } from 'react';
import { useProducts } from '@/hooks/useProducts';
import { Button } from '@/components/ui/button';
import { ProductFormDialog } from '@/components/products/ProductFormDialog';
import { DeleteConfirmDialog } from '@/components/products/DeleteConfirmDialog';
import type { Product } from '@/types/product';

function formatPrice(price: number | undefined, currency: string | undefined): string {
  if (price == null) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(price);
}

function StockBadge({ inventory }: { inventory: Product['inventory'] }) {
  if (!inventory) {
    return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">Unbekannt</span>;
  }
  if (!inventory.available) {
    return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">Nicht verfügbar</span>;
  }
  if (inventory.quantity != null && inventory.lowStockThreshold != null && inventory.quantity <= inventory.lowStockThreshold) {
    return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">Wenig ({inventory.quantity})</span>;
  }
  return (
    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
      Verfügbar{inventory.quantity != null ? ` (${inventory.quantity})` : ''}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

export function ProductsPage() {
  const { data, isLoading, isError, error, refetch } = useProducts();

  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);

  const products = data?.products ?? [];

  function handleCreate() {
    setEditProduct(undefined);
    setFormOpen(true);
  }

  function handleEdit(product: Product) {
    setEditProduct(product);
    setFormOpen(true);
  }

  function handleFormClose() {
    setFormOpen(false);
    setEditProduct(undefined);
  }

  function handleDeleteClose() {
    setDeleteTarget(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Produkte</h1>
          {!isLoading && !isError && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {products.length} {products.length === 1 ? 'Produkt' : 'Produkte'}
            </span>
          )}
        </div>
        <Button onClick={handleCreate}>Produkt erstellen</Button>
      </div>

      {isLoading && <LoadingSkeleton />}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">
            Produkte konnten nicht geladen werden: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {!isLoading && !isError && products.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-muted-foreground">Noch keine Produkte angelegt.</p>
          <Button variant="outline" className="mt-3" onClick={handleCreate}>
            Erstes Produkt erstellen
          </Button>
        </div>
      )}

      {!isLoading && !isError && products.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">SKU</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Preis</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Lager</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Kategorie</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.map((product) => (
                <tr key={product.productId} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{product.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{product.sku || '–'}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatPrice(product.price, product.currency)}</td>
                  <td className="px-4 py-3"><StockBadge inventory={product.inventory} /></td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {(product.attributes as Record<string, unknown>)?.categoryName as string || '–'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(product)}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(product)}
                      >
                        L&ouml;schen
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProductFormDialog
        open={formOpen}
        onClose={handleFormClose}
        product={editProduct}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onClose={handleDeleteClose}
        product={deleteTarget}
      />
    </div>
  );
}
